/**
 * Edit gift message modal — live preview, character limit, input filtering.
 * Preview script font: Biro Script Reduced (theme asset).
 */

/** Parent line item property — underscore prefix hides it at checkout. */
const GIFT_MESSAGE_PARENT_PROPERTY = '_Gift Message';

/** Legacy parent property key for carts created before the hidden key rollout. */
const GIFT_MESSAGE_PARENT_PROPERTY_LEGACY = 'Gift Message';

/** @type {RegExp} Best-effort emoji filter; not exhaustive for all Unicode sequences. */
const GIFT_MESSAGE_EMOJI_REGEX = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}]/gu;

/** @type {RegExp|undefined} */
let GIFT_MESSAGE_PICTographic_REGEX;

try {
  GIFT_MESSAGE_PICTographic_REGEX = new RegExp('\\p{Extended_Pictographic}', 'gu');
} catch (error) {
  GIFT_MESSAGE_PICTographic_REGEX = undefined;
}

class GiftMessageModal extends HTMLElement {
  constructor() {
    super();

    this.charLimit = parseInt(this.dataset.charLimit, 10) || 250;
    this.openingMessage = '';
    this.activeToggle = null;
    this.cartContext = null;
    this.returnFocusElement = null;
    this.previousBodyOverflow = '';
    this.isSaving = false;

    this.dialog = this.querySelector('.tw-gift-message-modal__dialog');
    this.input = this.querySelector('[data-gift-message-input]');
    this.counter = this.querySelector('[data-gift-message-counter]');
    this.previewText = this.querySelector('[data-gift-message-preview-text]');
    this.previewCard = this.querySelector('[data-gift-message-preview-card]');
    this.saveButton = this.querySelector('[data-gift-message-save]');
    this.placeholderText =
      this.previewText?.dataset.placeholder || 'your message will appear here...';

    this.boundOnInput = this.onInput.bind(this);
    this.boundOnSave = this.onSave.bind(this);
    this.boundOnClose = this.onClose.bind(this);
    this.boundOnKeydown = this.onKeydown.bind(this);
    this.boundSyncPreviewLayout = this.syncPreviewLayout.bind(this);
    this.previewLayoutObserver = new ResizeObserver(() => {
      this.syncPreviewLayout();
    });
  }

  connectedCallback() {
    this.input?.addEventListener('input', this.boundOnInput);
    this.saveButton?.addEventListener('click', this.boundOnSave);
    this.querySelectorAll('[data-gift-message-close]').forEach((el) => {
      el.addEventListener('click', this.boundOnClose);
    });
    document.addEventListener('keydown', this.boundOnKeydown);
    window.addEventListener('resize', this.boundSyncPreviewLayout);

    if (this.previewText) {
      this.previewLayoutObserver.observe(this.previewText);
    }

    const previewLogo = this.previewCard?.querySelector(
      '.tw-gift-message-modal__preview-logo',
    );

    if (previewLogo) {
      this.previewLayoutObserver.observe(previewLogo);
    }

    this.syncPreviewLayout();
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.boundOnInput);
    this.saveButton?.removeEventListener('click', this.boundOnSave);
    this.querySelectorAll('[data-gift-message-close]').forEach((el) => {
      el.removeEventListener('click', this.boundOnClose);
    });
    document.removeEventListener('keydown', this.boundOnKeydown);
    window.removeEventListener('resize', this.boundSyncPreviewLayout);
    this.previewLayoutObserver.disconnect();
  }

  /**
   * Keep preview text vertically centered on the card while clearing the logo.
   * Logo is out of flow; card min-height grows when text would overlap the logo.
   */
  syncPreviewLayout() {
    if (!this.previewCard || !this.previewText) {
      return;
    }

    const styles = getComputedStyle(this.previewCard);
    const baseMinHeight = parseFloat(
      styles.getPropertyValue('--gift-message-preview-base-min-height'),
    ) || 168;
    const logoGap = parseFloat(
      styles.getPropertyValue('--gift-message-preview-logo-gap'),
    ) || 28;
    const bottomOffset = parseFloat(
      styles.getPropertyValue('--gift-message-preview-logo-offset'),
    ) || 12;
    const logo = this.previewCard.querySelector(
      '.tw-gift-message-modal__preview-logo',
    );
    const messageHeight = this.previewText.offsetHeight;

    if (!logo) {
      this.previewCard.style.minHeight = `${Math.max(
        baseMinHeight,
        messageHeight,
      )}px`;
      return;
    }

    const logoBand = logoGap + logo.offsetHeight + bottomOffset;
    const minHeight = Math.max(
      baseMinHeight,
      messageHeight + logoBand * 2,
    );

    this.previewCard.style.minHeight = `${minHeight}px`;
  }

  /**
   * @returns {HTMLElement[]}
   */
  getFocusableElements() {
    if (!this.dialog) {
      return [];
    }

    const selector = [
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    return [...this.dialog.querySelectorAll(selector)].filter(
      (element) => element.offsetParent !== null,
    );
  }

  /**
   * Render at document root so cart drawer stacking cannot cover the modal.
   */
  ensureInBody() {
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }
  }

  /**
   * @param {{
   *   lineKey: string,
   *   addonLineKey?: string,
   *   message?: string,
   *   quantity?: number,
   *   returnFocus?: HTMLElement,
   * }} options
   */
  openForCart(options) {
    this.activeToggle = null;
    this.cartContext = {
      lineKey: options.lineKey,
      addonLineKey: options.addonLineKey || '',
      quantity: options.quantity || 1,
    };
    this.returnFocusElement = options.returnFocus || null;
    this.openingMessage = options.message || '';
    this.input.value = this.openingMessage;
    this.updatePreview(this.openingMessage);
    this.updateCounter(this.openingMessage.length);
    this.updateSaveState(this.openingMessage);

    this.ensureInBody();
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('is-gift-message-modal-open');

    requestAnimationFrame(() => {
      this.input?.focus();
      this.syncPreviewLayout();
    });
  }

  open(toggle, initialMessage = '') {
    this.cartContext = null;
    this.activeToggle = toggle;
    this.returnFocusElement = toggle.switchButton || toggle;
    this.openingMessage = initialMessage || '';
    this.input.value = this.openingMessage;
    this.updatePreview(this.openingMessage);
    this.updateCounter(this.openingMessage.length);
    this.updateSaveState(this.openingMessage);

    this.ensureInBody();
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('is-gift-message-modal-open');

    requestAnimationFrame(() => {
      this.input?.focus();
      this.syncPreviewLayout();
    });
  }

  close() {
    const focusTarget = this.returnFocusElement;

    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = this.previousBodyOverflow;
    document.body.classList.remove('is-gift-message-modal-open');
    this.activeToggle = null;
    this.cartContext = null;
    this.openingMessage = '';
    this.returnFocusElement = null;
    this.isSaving = false;
    this.saveButton?.removeAttribute('aria-busy');

    requestAnimationFrame(() => {
      focusTarget?.focus?.();
    });
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  sanitizeInput(value) {
    let next = value.replace(GIFT_MESSAGE_EMOJI_REGEX, '');

    if (GIFT_MESSAGE_PICTographic_REGEX) {
      next = next.replace(GIFT_MESSAGE_PICTographic_REGEX, '');
    }

    return next.slice(0, this.charLimit);
  }

  /**
   * @param {string} message
   */
  updatePreview(message) {
    if (!this.previewText) {
      return;
    }

    const trimmed = message.trim();

    if (trimmed === '') {
      this.previewText.textContent = this.placeholderText;
      this.previewText.classList.add('is-placeholder');
      requestAnimationFrame(() => {
        this.syncPreviewLayout();
      });
      return;
    }

    this.previewText.textContent = message;
    this.previewText.classList.remove('is-placeholder');
    requestAnimationFrame(() => {
      this.syncPreviewLayout();
    });
  }

  /**
   * @param {number} length
   */
  updateCounter(length) {
    if (!this.counter) {
      return;
    }

    this.counter.textContent = `${length} / ${this.charLimit}`;
    this.counter.classList.toggle('is-limit', length >= this.charLimit);
  }

  /**
   * @param {string} message
   */
  updateSaveState(message) {
    if (!this.saveButton) {
      return;
    }

    const hasChanged = message !== this.openingMessage;
    this.saveButton.disabled = !hasChanged;
  }

  onInput() {
    const sanitized = this.sanitizeInput(this.input.value);

    if (sanitized !== this.input.value) {
      this.input.value = sanitized;
    }

    this.updatePreview(sanitized);
    this.updateCounter(sanitized.length);
    this.updateSaveState(sanitized);
  }

  /**
   * @param {string} id
   * @param {number} quantity
   * @param {Record<string, string>} [properties]
   * @returns {Promise<object>}
   */
  async changeCartLine(id, quantity, properties) {
    /** @type {Record<string, unknown>} */
    const payload = { id, quantity };

    if (properties) {
      payload.properties = properties;
    }

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('Unable to update cart line item.');
    }

    return response.json();
  }

  /**
   * @param {string} lineKey
   * @returns {Promise<{ properties: Record<string, string>, quantity: number }|null>}
   */
  async fetchCartLine(lineKey) {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Unable to load cart.');
    }

    const cart = await response.json();
    const lineItem = cart.items.find((item) => item.key === lineKey);

    if (!lineItem) {
      return null;
    }

    return {
      properties: { ...(lineItem.properties || {}) },
      quantity: lineItem.quantity || 1,
    };
  }

  /**
   * @param {string} message
   * @returns {Promise<void>}
   */
  async saveCartMessage(message) {
    const context = this.cartContext;

    if (!context?.lineKey) {
      throw new Error('Missing cart line context for gift message save.');
    }

    const cartLine = await this.fetchCartLine(context.lineKey);

    if (!cartLine) {
      throw new Error('Unable to find cart line for gift message save.');
    }

    const mainProperties = {
      ...cartLine.properties,
    };

    if (message === '') {
      delete mainProperties[GIFT_MESSAGE_PARENT_PROPERTY];
      delete mainProperties[GIFT_MESSAGE_PARENT_PROPERTY_LEGACY];
    } else {
      mainProperties[GIFT_MESSAGE_PARENT_PROPERTY] = message;
      delete mainProperties[GIFT_MESSAGE_PARENT_PROPERTY_LEGACY];
    }

    await this.changeCartLine(
      context.lineKey,
      cartLine.quantity,
      mainProperties,
    );

    if (context.addonLineKey) {
      if (message === '') {
        await this.changeCartLine(context.addonLineKey, 0);
      } else {
        const addonLine = await this.fetchCartLine(context.addonLineKey);

        if (addonLine) {
          const addonProperties = {
            ...addonLine.properties,
            'Gift Message': message,
            '__gift_message_addon': 'true',
          };

          await this.changeCartLine(
            context.addonLineKey,
            addonLine.quantity,
            addonProperties,
          );
        }
      }
    }

    if (typeof reBuildCart === 'function') {
      reBuildCart();
    }
  }

  setSavingState(isSaving) {
    this.isSaving = isSaving;

    if (!this.saveButton) {
      return;
    }

    this.saveButton.disabled = isSaving;
    this.saveButton.setAttribute('aria-busy', isSaving ? 'true' : 'false');
  }

  async onSave(event) {
    event.preventDefault();

    if (this.isSaving) {
      return;
    }

    const toggle = this.activeToggle;
    const message = this.sanitizeInput(this.input.value).trim();

    if (this.cartContext) {
      this.setSavingState(true);

      try {
        await this.saveCartMessage(message);
        this.close();
      } catch (error) {
        console.error(error);
      } finally {
        this.setSavingState(false);
      }

      return;
    }

    if (message === '') {
      toggle?.clearSavedMessage();
      this.close();
      return;
    }

    toggle?.applySavedMessage(message);
    this.close();
  }

  onClose(event) {
    event.preventDefault();

    if (this.isSaving) {
      return;
    }

    const toggle = this.activeToggle;
    const hadSavedMessage = Boolean(toggle?.savedMessage?.trim());
    const isCartEdit = Boolean(this.cartContext);

    this.close();

    if (isCartEdit) {
      return;
    }

    if (toggle && !hadSavedMessage) {
      toggle.setEnabled(false, { skipModal: true });
    }
  }

  onKeydown(event) {
    if (!this.classList.contains('is-open')) {
      return;
    }

    if (event.key === 'Escape') {
      this.onClose(event);
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusable = this.getFocusableElements();
    if (focusable.length === 0) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('tw-gift-message-modal')) {
  customElements.define('tw-gift-message-modal', GiftMessageModal);
}

/**
 * Keep a single modal instance when theme and cart drawer both inject markup.
 * Prefer the theme-level modal (outside cart holder) and portal it to body.
 */
function dedupeGiftMessageModals() {
  const modals = [...document.querySelectorAll('tw-gift-message-modal')];

  if (modals.length <= 1) {
    if (modals[0]) {
      if (modals[0].parentElement !== document.body) {
        document.body.appendChild(modals[0]);
      }
    }

    return;
  }

  const cartHolder = document.querySelector('.vue-cart.slider_cart_holder');
  const preferred =
    modals.find((modal) => !cartHolder?.contains(modal)) || modals[0];

  modals.forEach((modal) => {
    if (modal !== preferred) {
      modal.remove();
    }
  });

  if (preferred.parentElement !== document.body) {
    document.body.appendChild(preferred);
  }
}

/**
 * Cart drawer EDIT — opens modal in cart edit mode.
 */
function initGiftMessageCartEdit() {
  document.addEventListener('click', (event) => {
    const editButton = event.target.closest('[data-gift-message-edit]');

    if (!editButton || editButton.disabled) {
      return;
    }

    event.preventDefault();

    const modal = document.getElementById('tw-gift-message-modal');

    if (!modal || typeof modal.openForCart !== 'function') {
      return;
    }

    modal.openForCart({
      lineKey: editButton.dataset.lineKey,
      addonLineKey: editButton.dataset.addonLineKey || '',
      message: editButton.getAttribute('data-gift-message') || '',
      quantity: parseInt(editButton.dataset.lineQuantity, 10) || 1,
      returnFocus: editButton,
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', dedupeGiftMessageModals);
} else {
  dedupeGiftMessageModals();
}

initGiftMessageCartEdit();
